'use client'

import { Button, Checkbox, Input, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'

import TwoColumnsWithBackgroundImage from '@/components/TwoColumnsWithBackgroundImage'
import { useNewsletter } from '@/hooks/useNewsLetter'

// eslint-disable-next-line complexity
const Money2020Form: FC<{ hubspotFormId: string }> = ({ hubspotFormId }) => {
    const { palette } = LuneTheme
    const { smScreen, xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(hubspotFormId, [
        'How many Payhawk clients adopted their new Lune-powered emissions reporting feature? Tell us by submitting the form below.',
        'I am happy to receive marketing communications from Lune',
    ])

    const [answer, setAnswer] = useState<string>('')
    const [name, setName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [{ email, edited: editedEmail }, setEmail] = useState<{ email: string; edited: boolean }>(
        { email: '', edited: false },
    )
    const [company, setCompany] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [answerError, setAnswerError] = useState<boolean>(false)
    const [nameError, setNameError] = useState<boolean>(false)
    const [lastNameError, setLastNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [phoneError, setPhoneError] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>()
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false)

    useEffect(() => {
        if (!editedEmail) {
            setEmailError(false)
            return
        }
        setEmailError(!isValidEmail(email))
    }, [email, editedEmail, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    useEffect(() => {
        setLastNameError(false)
    }, [lastName])

    useEffect(() => {
        setCompanyError(false)
    }, [company])

    useEffect(() => {
        setAnswerError(false)
    }, [answer])

    useEffect(() => {
        setPhoneError(false)
    }, [phone])

    const onSubmit = async () => {
        const validEmail = isValidEmail(email)
        const validCompany = company.length

        if (!validEmail || !validCompany) {
            setEmailError(!validEmail)
            setCompanyError(!validCompany)

            return
        }

        const data = await toHubspot({
            firstname: name,
            lastname: lastName,
            email,
            company,
            money2020Answer: answer,
            phone,
            legalConsent: marketingConsent ? 'marketing' : 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setLastName('')
            setEmail({ email: '', edited: false })
            setCompany('')
            setAnswer('')
            setPhone('')

            gtag('event', 'request_money2020_cap', {})
        }
    }

    return (
        <TwoColumnsWithBackgroundImage
            backgroundImage={'/images/money2020-desktop.png'}
            mobileBackgroundImage={'/images/money2020-mobile.png'}
            rightSide={<Box sx={{ height: smScreen ? '268px' : xsScreen ? '268px' : 'auto' }} />}
            reversedOnMobile
            leftSide={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: mdScreen ? 3 : 4,
                    }}
                >
                    {success ? (
                        <Box
                            sx={{
                                height: mdScreen ? '508px' : '572px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: mdScreen ? 4 : 9,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                Thanks for playing!
                                <br />
                                <br />
                                {`Claim your free f*ck emissions cap from the box below Lune's Money2020 stand.`}
                                <br />
                                <br />
                                Go on! Lift the lid and take one :)
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                Money2020: Claim your f*ck emissions cap
                            </Text>
                            <Text variant={mdScreen ? 'body2' : 'body1'}>
                                How many Payhawk clients adopted their new Lune-powered emissions
                                reporting feature?
                                <br />
                                <br />
                                Tell us by submitting the form below.
                            </Text>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%',
                                    '.MuiInputBase-root ': {
                                        width: '100%',
                                    },
                                }}
                            >
                                <Input
                                    placeholder={'Answer'}
                                    error={answerError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={answer}
                                    onChange={(e) => setAnswer(e.target.value)}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        justifyContent: 'space-between',
                                        flexDirection: 'row',
                                        gap: 1,
                                        width: '100%',
                                    }}
                                >
                                    <Input
                                        placeholder={'Name'}
                                        error={nameError}
                                        value={name}
                                        sx={{ backgroundColor: palette.White }}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                    <Input
                                        placeholder={'Surname'}
                                        error={lastNameError}
                                        value={lastName}
                                        sx={{ backgroundColor: palette.White }}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                </Box>
                                <Input
                                    placeholder={'Company name'}
                                    error={companyError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                                <Input
                                    placeholder={'Work email'}
                                    error={emailError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={email}
                                    onChange={(e) =>
                                        setEmail({ email: e.target.value, edited: true })
                                    }
                                />
                                <Input
                                    placeholder={'Phone'}
                                    error={phoneError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />
                                <Box pt={4}>
                                    <Checkbox
                                        sx={{ marginRight: '12px' }}
                                        checked={marketingConsent}
                                        onChange={(e) => setMarketingConsent(e.target.checked)}
                                    />

                                    <Text
                                        variant="body3"
                                        onClick={() => setMarketingConsent(!marketingConsent)}
                                        sx={{ '&:hover': { cursor: 'pointer' } }}
                                    >
                                        I am happy to receive marketing communications from Lune
                                    </Text>
                                </Box>
                            </Box>
                            <Button
                                onClick={() => {
                                    setEmail({ email, edited: true })
                                    onSubmit()
                                }}
                                leftIcon={<ArrowCircleRightIcon />}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                Get my free cap
                            </Button>
                        </>
                    )}
                </Box>
            }
        />
    )
}

export default Money2020Form
